import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AuthorizationMatrix } from 'app/core/store/session/authorization/authorization.interface';
import { CcrLineItem } from 'app/core/interfaces/florder.interface';
import { appSettings } from 'app/appSettings';
import { ContractRestrictions } from 'app/core/store/contract-details/contract-details.interface';
import { util } from 'app/core/util/util';
import { Definition } from 'app/core/store/definitions/definition.interface';
import { Definitions } from 'app/core/store/definitions/definitions.interface';
import { FlordersFilter } from 'app/core/interfaces/florders-filter.interface';
import { Customer } from 'app/core/store/session/user-context/user-context.interface';
import { Order } from 'app/core/interfaces/order.interface';
import { easeInOutTrigger } from 'app/animations';
import { ScrollService } from 'app/core/services/scroll.service';
import { takeUntil } from 'rxjs/operators';
import { Destroyable } from '../../../core/util/destroyable';
import { BreakpointObserver } from '@angular/cdk/layout';
import { LayoutBreakpoints } from '../../../core/util/layout-breakpoints';

interface OrderEdit {
  id: string;
  etmOrderNumber: string;
  salesOrderNumber: string;
  customerReferenceNumber: string;
  fromId: string;
  toId: string;
  checked: boolean;
  collapsed: boolean;
  statusId: string;
}

@Component({
  selector: 'app-order-list',
  templateUrl: './order-list.component.html',
  styleUrls: ['../../florders/florder-list/florder-list.scss', './order-list.component.scss'],
  animations: [easeInOutTrigger('easeInOut', 0)],
})
export class OrderListComponent extends Destroyable implements OnInit, OnChanges {
  private initialized = false;
  private orderEdits: { [key: string]: OrderEdit } = {};
  visibleCorrectionsOrderIds = [];
  allCorrectionsOrderIds = [];
  showAllCorrections = true;

  dateFormat: string = appSettings.DATE_FORMAT_ANGULAR;
  showAll: boolean = false;

  smallScreen: boolean;

  @Input() orders: Order[];
  @Input() readonly definitions: Definitions;
  @Input() isLoading: boolean;
  @Input() isDownloading: boolean;
  @Input() isSuperUser: boolean;
  @Input() filter: FlordersFilter;
  @Input() totalItems: number;
  @Input() authorization: AuthorizationMatrix;
  @Input() restrictions: ContractRestrictions;
  @Input() customersAllowedCreateOrUpdate: Customer[];

  @Output() downloadFile = new EventEmitter<string>();
  @Output() openOrder = new EventEmitter<Order>();
  @Output() pageChange = new EventEmitter<number>();
  @Output() sortChange = new EventEmitter<{ sortField: string; sortAscending: boolean }>();
  @Output() copyOrder = new EventEmitter<Order>();
  @Output() createCCR = new EventEmitter<Order>();
  @Output() editCCR = new EventEmitter<Order>();
  @Output() asTemplate = new EventEmitter<Order>();
  @Output() acceptDeviation = new EventEmitter<string>();
  @Output() rejectDeviation = new EventEmitter<string>();
  @Output() downloadDocument = new EventEmitter<string>();
  @Output() downloadCCRDocument = new EventEmitter<string>();
  @Output() itemsPerPageChange = new EventEmitter<number>();

  private filterId: number;

  constructor(private scrollService: ScrollService, private breakpointObserver: BreakpointObserver) {
    super();
  }

  ngOnInit(): void {
    this.initialized = true;
    this.updateChecks();
    LayoutBreakpoints.matches(this.breakpointObserver, LayoutBreakpoints.smallScreenOrLess).pipe(
      takeUntil(this.destroyed$),
    ).subscribe(smallScreen => this.smallScreen = smallScreen);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.orders) {
      this.scrollService.scrollToElement('order-list', 10);
      // TODO BFR suggestion: clone orders, use order object themselves to store local state
      this.ordersChanged();
    }
    if (changes.filter) {
      if (this.filter && this.filter.filterId !== this.filterId) {
        this.filterId = this.filter.filterId;
        this.showAllCorrections = false;
        this.toggleShowAllCorrections();
      }
    }
  }

  /**
   * Update form objects for the current subset of orders shown. Use already stored data from previous edit actions for current query
   * formEdit => formGroup controls
   */
  updateChecks() {
    this.orders.forEach(order => {
      const id = `${order.etmOrderNumber}-${order.salesOrderNumber}`;
      let orderEdit: OrderEdit = this.orderEdits[id];
      if (!orderEdit) {
        orderEdit = {
          id,
          etmOrderNumber: `${order.etmOrderNumber}`,
          salesOrderNumber: `${order.salesOrderNumber}`,
          customerReferenceNumber: order.customerRefNumber, // TODO is this needed ?
          fromId: order.from.id,
          toId: order.to.id,
          checked: false,
          collapsed: !this.showAll,
          statusId: order.status && (order.status as Definition).id,
        } as OrderEdit;
        this.orderEdits[id] = orderEdit;
      }
    });
  }

  onPageChange(pageNr) {
    this.pageChange.emit(pageNr);
    this.scrollService.scrollToElement('order-list', 40);
  }

  /**
   * Check if flow is collapsed or not
   * @param {string} etmOrderNumber of order to check. Order shall always exist in orderEdit object since that object is created by updateChecks (when order is set)
   * @param salesOrderNumber
   * @returns {boolean} collapsed or not
   */
  isCollapsed(etmOrderNumber: string, salesOrderNumber: string): boolean {
    const id = `${etmOrderNumber}-${salesOrderNumber}`;
    return this.orderEdits[id].collapsed;
  }

  /**
   * set collapsed boolean to other value
   * @param {string} etmOrderNumber of order to check. Order shall always exist in orderEdit object since that object is created by updateChecks (when order is set)
   * @param salesOrderNumber
   * @returns {boolean} collapsed or not
   */
  toggleCollapsed(etmOrderNumber: string, salesOrderNumber: string): boolean {
    const id = `${etmOrderNumber}-${salesOrderNumber}`;
    return (this.orderEdits[id].collapsed = !this.orderEdits[id].collapsed);
  }

  toggleCollapseAll() {
    Object.keys(this.orderEdits)
      .map(key => this.orderEdits[key])
      .forEach(orderEdit => (orderEdit.collapsed = this.showAll));
    this.showAll = !this.showAll;
  }

  sort(field: string) {
    util.sortHelper(this, field);
  }

  sortBnd = this.sort.bind(this);

  preventRouting(event) {
    event.stopPropagation();
  }

  openMenu(event) {
    event.stopPropagation();
  }

  acceptDeviationClicked($event, order: Order) {
    this.preventRouting($event);
    if (!order.ccr.UI.submittingAccept && !order.ccr.UI.submittingReject) {
      this.acceptDeviation.emit(order.ccr.ccrNumber);
    }
  }

  rejectDeviationClicked($event, order: Order) {
    this.preventRouting($event);
    if (!order.ccr.UI.submittingAccept && !order.ccr.UI.submittingReject) {
      this.rejectDeviation.emit(order.ccr.ccrNumber);
    }
  }

  getLoadedQuantity(ccrLine: CcrLineItem) {
    if (ccrLine.loaded) {
      return ccrLine.loaded.packingQuantity || ccrLine.loaded.numberOfPallets;
    }
  }

  getUnloadedQuantity(ccrLine: CcrLineItem) {
    if (ccrLine.unloaded) {
      return ccrLine.unloaded.packingQuantity || ccrLine.unloaded.numberOfPallets;
    }
  }

  getDifference(ccrLine: CcrLineItem) {
    if (ccrLine.difference) {
      return ccrLine.difference.packingQuantity || ccrLine.difference.numberOfPallets;
    }
  }

  showCcrButtons(order: Order) {
    const statusId: string = typeof order.status === 'string' ? (order.status as string) : (order.status as Definition).id;
    const superUserCanView = this.isSuperUser && this.checkIfCustomerIsInCreateOrUpdate(order.customerNumber.toString());
    const orderHasCorrectStatus = order.ccr.open && (statusId === '07' || statusId === '08');

    return this.isSuperUser ? superUserCanView && orderHasCorrectStatus : (this.authorization.ORDER.ACCEPT || this.authorization.ORDER.CANCEL) && orderHasCorrectStatus;
  }

  canCreateCcr(order: Order): boolean {
    const superUserCanView = this.isSuperUser && this.checkIfCustomerIsInCreateOrUpdate(order.customerNumber.toString());
    return this.isSuperUser ? superUserCanView && order.canCreateCcr : this.authorization.ORDER.CREATE_CCR && order.canCreateCcr;
  }

  canEditCcr(order: Order): boolean {
    const superUserCanView = this.isSuperUser && this.checkIfCustomerIsInCreateOrUpdate(order.customerNumber.toString());
    return this.isSuperUser ? superUserCanView && order.canEditCcr : this.authorization.ORDER.CREATE_CCR && order.canEditCcr;
  }

  canCopyOrder(order: Order): boolean {
    const superUserCanView = this.isSuperUser && this.checkIfCustomerIsInCreateOrUpdate(order.customerNumber.toString());
    return this.isSuperUser ? superUserCanView : this.authorization.ORDER.CREATE;
  }

  canCreateTemplate(order: Order): boolean {
    const superUserCanView = this.isSuperUser && this.checkIfCustomerIsInCreateOrUpdate(order.customerNumber.toString());
    return this.isSuperUser ? superUserCanView : this.authorization.ORDER.CREATE_TEMPLATE;
  }

  canDownloadCcrDocument(order: Order): boolean {
    return !!order.ccr.ccrNumber;
  }

  canShowMenu(order: Order): boolean {
    // check if the 3 dots must be shown, if a user cant do anything,
    // no point in showing the dots
    return this.canCreateCcr(order) || this.canCopyOrder(order) || this.canCreateTemplate(order) || this.canDownloadCcrDocument(order) ? true : false;
  }

  checkIfCustomerIsInCreateOrUpdate(customerId: string): boolean {
    if (this.customersAllowedCreateOrUpdate) {
      return !!this.customersAllowedCreateOrUpdate.find(c => {
        return c.id === customerId;
      });
    }
    return false;
  }

  orderClicked(order: Order) {
    if (this.authorization.ORDER.GET) {
      this.openOrder.emit(order);
    }
  }

  correctionsToggleClicked(order: Order, event: MouseEvent) {
    event.stopPropagation();
    const index = this.visibleCorrectionsOrderIds.indexOf(order.etmOrderNumber);
    if (index < 0) {
      this.visibleCorrectionsOrderIds.push(order.etmOrderNumber);
    } else {
      this.visibleCorrectionsOrderIds.splice(index, 1);
    }
    this.calculateShowAllCorrectionsBoolean();
  }

  correctionsClicked(order: Order, event: MouseEvent) {
    event.stopPropagation();
    const index = this.visibleCorrectionsOrderIds.indexOf(order.etmOrderNumber);
    if (index >= 0) {
      this.visibleCorrectionsOrderIds.splice(index, 1);
    }
    this.calculateShowAllCorrectionsBoolean();
  }

  private calculateShowAllCorrectionsBoolean() {
    if (this.showAllCorrections && this.visibleCorrectionsOrderIds.length === 0) {
      this.showAllCorrections = false;
    } else if (!this.showAllCorrections && this.visibleCorrectionsOrderIds.length === this.allCorrectionsOrderIds.length) {
      this.showAllCorrections = true;
    }
  }

  correctionsVisible(order: Order): boolean {
    return this.visibleCorrectionsOrderIds.indexOf(order.etmOrderNumber) >= 0;
  }

  toggleShowAllCorrections() {
    if (this.showAllCorrections) {
      this.showAllCorrections = false;
      this.visibleCorrectionsOrderIds = [];
    } else {
      this.showAllCorrections = true;
      this.visibleCorrectionsOrderIds = [...this.allCorrectionsOrderIds];
    }
  }

  private ordersChanged() {
    this.updateChecks();
    this.allCorrectionsOrderIds = this.orders.filter(order => order.corrections.showCorrections).map(order => order.etmOrderNumber);
    if (this.showAllCorrections) {
      this.visibleCorrectionsOrderIds = [...this.allCorrectionsOrderIds];
    } else {
      this.visibleCorrectionsOrderIds = [];
    }
  }
}
